/** @format */

import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	Component,
	input,
	OnInit,
	output,
	signal,
	PLATFORM_ID,
	Inject,
	ChangeDetectorRef
} from '@angular/core';

import { ProductAnalyticsService } from '@shared/data-access/services';
import { IsBotService, WINDOW } from '@shared/utils/services';
import { ImageSwatchComponent } from '../image-swatch/image-swatch.component';
import { HomefieldTextLinkModule } from '@homefield';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'plp-components-product-card-image-swatches',
	imports: [CommonModule, ImageSwatchComponent, HomefieldTextLinkModule, HmfActionTextComponent],
	templateUrl: './product-card-image-swatches.component.html',
	styleUrls: ['./product-card-image-swatches.component.scss']
})
export class ProductCardImageSwatchesComponent implements OnInit {
	assetSeoUrl = input('');
	colors = input<string[]>([]);
	ecode = input('');
	isLazy = input(true);

	selectedSwatchColorChange = output<string>();
	extraColorsClick = output<void>();

	IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';
	maxColorSwatchesToShow = signal(2);
	selectedSwatchColor = signal('');
	moreColorsButtonAriaLabel = signal('');
	remainingColors = signal(0);
	imageSrcMap: { [colorKey: string]: string } = {};

	constructor(
		private readonly isBotService: IsBotService,
		private readonly analyticsService: ProductAnalyticsService,
		private readonly cdr: ChangeDetectorRef,
		@Inject(PLATFORM_ID) private readonly platformId,
		@Inject(WINDOW) protected window: Window
	) {}

	ngOnInit(): void {
		if (!this.colors()?.length) {
			return;
		}

		this.selectedSwatchColor.set(this.colors()[0]);

		this.setNumberOfSwatchesToShow();
		this.setRemainingColorsAndMoreColorsAriaLabel();
		this.buildImageSrcMap();
	}

	trackBySwatch(index: number, item: string): string {
		return `${index}-${item}`;
	}

	setImageColor(color: string): void {
		if (color === this.selectedSwatchColor()) {
			return;
		}

		this.selectedSwatchColor.set(color);
		this.selectedSwatchColorChange.emit(color);
	}

	getImageSource(color: string): string {
		const sanitizedColor = color.replace(/[^\w]/gi, '_');
		return `${this.IMAGE_HOST}/${this.ecode()}_${sanitizedColor}_is/?$DSG_PDPThumb$`;
	}

	extraColorsClicked(): void {
		this.extraColorsClick.emit();
	}

	/**
	 * @description Posts to rumrunner when a missing product image is detected
	 */
	handleImageError(event: { color: string; requestedImg: string }): void {
		if (
			isPlatformBrowser(this.platformId) &&
			!this.isBotService.isBot(window.navigator.userAgent)
		) {
			this.analyticsService.emitMissingImageEvent({
				pageUrl: window.location.href,
				requestedImg: event.requestedImg,
				ecode: this.ecode(),
				color: event.color
			});
		}
	}

	private setNumberOfSwatchesToShow(): void {
		if (!isPlatformBrowser(this.platformId) || !this.colors()?.length) {
			return;
		}

		const widthMediaQuery = matchMedia('(max-width: 1531px)');

		// Initial check of the media query
		this.maxColorSwatchesToShow.set(widthMediaQuery.matches ? 2 : 4);

		// Check when window width passes the media query
		widthMediaQuery.onchange = (event) => {
			this.maxColorSwatchesToShow.set(event.matches ? 2 : 4);
			this.setRemainingColorsAndMoreColorsAriaLabel();
			this.cdr.detectChanges();
		};
	}

	private setRemainingColorsAndMoreColorsAriaLabel(): void {
		this.remainingColors.set(this.colors().length - this.maxColorSwatchesToShow());
		this.moreColorsButtonAriaLabel.set(
			`Show ${this.remainingColors()} more color option${this.remainingColors() > 1 ? 's' : ''}.`
		);
	}

	// To avoid calling method directly in template, this method builds a map of color to image source
	// so that the image sources can be pre-calculated and accessed directly in the template for better performance.
	private buildImageSrcMap(): void {
		if (!this.colors()) return;
		this.imageSrcMap = this.colors().reduce(
			(map, color) => {
				map[color] = this.getImageSource(color);
				return map;
			},
			{} as { [key: string]: string }
		);
	}
}
